import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import { filterBySearch, toGridRows } from '../_helpers/ai-usage-rows.helper';
import { AiUsageDimension } from '../enums/aiUsageDimension.enum';
import { SecurityProfileEnum } from '../enums/securityProfile.enum';
import { AiUsageByUserResponse, AiUsageGridRow, AiUsageTotals } from '../models/aiUsageByUser.model';
import { ColumnDetails } from '../models/columnDetails.model';
import { GridModel } from '../shared/grid/grid.model';
import { IGridService } from '../shared/grid/grid.service';
import { AiAnalystMetricsService } from './aiAnalystMetrics.service';
import { AuthService } from './auth.service';

export const AI_USAGE_MONTH_FILTER_KEY = 'month';
export const AI_USAGE_GROUP_BY_FILTER_KEY = 'groupBy';

interface AiUsageGridFilter {
  [AI_USAGE_MONTH_FILTER_KEY]: string;
  [AI_USAGE_GROUP_BY_FILTER_KEY]: AiUsageDimension;
}

@Injectable({ providedIn: 'root' })
export class AiUsageByUserGridService implements IGridService<AiUsageGridRow> {
  private readonly cancelPrevious$ = new Subject<void>();
  private readonly totalsSubject = new BehaviorSubject<AiUsageTotals | null>(null);

  constructor(
    private readonly metricsService: AiAnalystMetricsService,
    private readonly authService: AuthService
  ) { }

  get totals$(): Observable<AiUsageTotals | null> {
    return this.totalsSubject.asObservable();
  }

  get(filter: string, orderBy: string, sort: string, page: number, pageSize: number, filterBy: string): Observable<GridModel<AiUsageGridRow>> {
    const { month, groupBy } = JSON.parse(filterBy) as AiUsageGridFilter;
    const crossTenant = this.authService.getCurrentUser().userType === SecurityProfileEnum.SuperAdmin;

    this.cancelPrevious$.next();
    return this.metricsService.getUsageByUser(month, groupBy, crossTenant).pipe(
      takeUntil(this.cancelPrevious$),
      map(response => response ?? this.emptyResponse(month)),
      tap(response => this.totalsSubject.next(response.totals)),
      map(response => {
        const rows = toGridRows(filterBySearch(response.rows, filter ?? ''));
        return new GridModel<AiUsageGridRow>(rows.slice(page * pageSize, (page + 1) * pageSize), rows.length);
      })
    );
  }

  getFieldConfiguration(): Observable<{ [property: string]: ColumnDetails }> {
    return of({});
  }

  private emptyResponse(month: string): AiUsageByUserResponse {
    return { month, totals: { creditsUsed: 0, activeUserCount: 0, questionCount: 0, reportCount: 0 }, rows: [] };
  }
}
